import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { browserHistory } from 'react-router';
import PropTypes from 'prop-types';
import { debounce, size } from 'lodash';

import updateCurrentCommunity from '../../redux/communities/sagas/updateCurrentCommunity';
import { ACCESS_DENIED_COMMUNITIES, NOT_FOUND } from '../../constants/routes';
import { RESTART_ON_REMOUNT } from '../../redux/sagaInjectors';
import { useInjectSaga } from '../../redux/injectSaga';
import {
  COMMUNITIES_COMMON,
  COMMUNITY_DESCRIPTION,
} from '../../constants/strings';
import { isDesktop } from '../../utils/mobileHelpers';
import { useLazyLoad } from '../../hocs/useLazyLoad';
import { COLORS } from '../../constants/styles';
import Loader from '../common/Loader';
import Drawer from '../Drawer';
import About from './About';
import SectionTitle from './SectionTitle';
import { enhance } from './enhance';
import Banner from './Banner';
import Heading from './Heading';
import Posts from './Posts';
import NewPostsBtn from './NewPostsBtn';

const Community = ({
  intl: { formatMessage },
  postsList,
  isDrawerVisible: { visible },
  community,
  communityLoading,
  fetchCommunity,
  fetchPosts,
  resetCommunity,
  updateImage,
  removeImage,
  imageProcessing,
  fetchMorePosts,
  setScrollStatus,
  communityId,
  shouldLoadMore,
  isModerator,
  parentRef,
  temporaryPostList,
  addPostsList,
  resetScrollStatus,
}) => {
  useInjectSaga({
    key: 'updateCurrentCommunity',
    saga: updateCurrentCommunity,
    mode: RESTART_ON_REMOUNT,
  });

  const { currentUserRole, banner, error, type, name } = community;

  const rootRef = useRef();
  const [isMobileDrawerOpen, showDrawer] = useDrawer(visible);
  const loadMore = useCallback(() => shouldLoadMore && fetchMorePosts(), [
    shouldLoadMore,
  ]);
  const handleBannerCrop = useCallback(data => {
    updateImage({ banner: data });
  }, []);
  const handleBannerRemove = useCallback(() => {
    removeImage({ data: { banner: true } });
  }, []);
  const drawerClassNames = isMobileDrawerOpen
    ? 'absolute absolute--fill bg-washed-blue pt3 overflow-y'
    : '';

  useNotFoundRedirect(communityId);
  useErrorHandling(error);
  useLazyLoad({ node: rootRef, path: ['parentNode'], loadMore });
  useEffect(() => {
    fetchCommunity();
    fetchPosts();

    return () => {
      resetCommunity();
    };
  }, []);

  const [scrollTop, setScrollTop] = useState(0);
  const [checkScroll, setScrollChecker] = useState(true);
  const hasNewPosts = size(temporaryPostList);

  useEffect(() => {
    if (currentUserRole) {
      const handleScroll = debounce(() => {
        setScrollTop(parentRef.scrollTop);
      }, 20);
      if (parentRef) {
        parentRef.addEventListener('scroll', handleScroll);

        return () => {
          parentRef.removeEventListener('scroll', handleScroll);
          resetScrollStatus();
        };
      }
    }
  }, [parentRef, currentUserRole]);

  useEffect(() => {
    if (currentUserRole) {
      if (scrollTop > 300 && checkScroll) {
        setScrollChecker(false);
        setScrollStatus({ scrolled: true });
      } else if (scrollTop < 300) {
        setScrollChecker(true);
        setScrollStatus({ scrolled: false });
        if (hasNewPosts) {
          addPostsList(temporaryPostList);
          resetScrollStatus();
        }
      }
    }
  }, [scrollTop, temporaryPostList]);

  const handleScrollTop = () => {
    parentRef.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (communityLoading) {
    return <Loader />;
  }

  return (
    <div styleName="root" ref={rootRef}>
      <If condition={hasNewPosts}>
        <NewPostsBtn
          title={formatMessage(COMMUNITIES_COMMON.seeNewPosts)}
          handleClick={() => handleScrollTop()}
        />
      </If>
      <Banner
        onImageRemove={handleBannerRemove}
        onImageCrop={handleBannerCrop}
        isModerator={isModerator}
        image={banner}
      />
      {imageProcessing && <Loader styleName="imageProcessing" />}
      <div styleName="wrapper">
        <Choose>
          <When condition={!communityLoading}>
            <Heading
              offset={isDesktop()}
              updateImage={updateImage}
              removeImage={removeImage}
            />
          </When>
          <Otherwise>
            <Loader />
          </Otherwise>
        </Choose>
        <Choose>
          <When condition={!communityLoading}>
            <div styleName="content">
              <div styleName="posts">
                <Posts
                  isModerator={isModerator}
                  list={postsList}
                  communityId={communityId}
                  currentUserRole={currentUserRole}
                  type={type}
                  communityName={name}
                />
              </div>
              <div styleName="sidebar">
                <If condition={showDrawer}>
                  <Drawer
                    opened={showDrawer}
                    position="right"
                    style={{
                      background: !isMobileDrawerOpen && 'transparent',
                      color: COLORS.grayDark,
                      float: 'none',
                      minHeight: 200,
                      boxShadow: 'none',
                      zIndex: 100,
                    }}
                    className={drawerClassNames}
                    width={300}
                    scrollable={false}
                  >
                    <div styleName={isMobileDrawerOpen && 'sidebar__content'}>
                      <SectionTitle
                        text={formatMessage(COMMUNITY_DESCRIPTION)}
                      />
                      <About community={community} />
                    </div>
                  </Drawer>
                </If>
              </div>
            </div>
          </When>
          <Otherwise>
            <Loader />
          </Otherwise>
        </Choose>
      </div>
    </div>
  );
};

Community.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  postsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  isDrawerVisible: PropTypes.object.isRequired,
  community: PropTypes.object.isRequired,
  communityLoading: PropTypes.bool.isRequired,
  shouldLoadMore: PropTypes.bool.isRequired,
  fetchCommunity: PropTypes.func.isRequired,
  fetchPosts: PropTypes.func.isRequired,
  resetCommunity: PropTypes.func.isRequired,
  fetchMorePosts: PropTypes.func.isRequired,
  updateImage: PropTypes.func.isRequired,
  removeImage: PropTypes.func.isRequired,
  communityId: PropTypes.number.isRequired,
  imageProcessing: PropTypes.bool.isRequired,
  isModerator: PropTypes.bool.isRequired,
  parentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  setScrollStatus: PropTypes.func,
  temporaryPostList: PropTypes.arrayOf(PropTypes.object),
  resetScrollStatus: PropTypes.func,
  addPostsList: PropTypes.func,
};

Community.defaultProps = {
  parentRef: null,
  temporaryPostList: [],
  addPostsList: null,
  resetScrollStatus: null,
  setScrollStatus: null,
};

export default enhance(Community);

/** C U S T O M   H O O K S */
function useErrorHandling(error) {
  useEffect(() => {
    if (!error) {
      return;
    }

    switch (error.status) {
      case 404:
        return browserHistory.push(NOT_FOUND);
      case 403:
        return browserHistory.push(ACCESS_DENIED_COMMUNITIES);
    }
  }, [error]);
}

function useNotFoundRedirect(communityId) {
  useEffect(() => {
    if (Number.isNaN(communityId)) {
      browserHistory.push(NOT_FOUND);
    }
  }, [communityId]);
}

function useDrawer(visible) {
  return useMemo(() => {
    const isMobileOpen = !isDesktop() && visible;
    const showDrawer = isDesktop() || isMobileOpen;

    return [isMobileOpen, showDrawer];
  }, [visible]);
}



// WEBPACK FOOTER //
// ./src/components/Community/index.jsx